import { motion, useReducedMotion, useWillChange } from 'framer-motion';
import { ComponentProps, ReactNode, forwardRef } from 'react';
import cn from '@/lib/cs';
import useHaptics from '@/hooks/useHaptics';

const IconButton = forwardRef<
	HTMLButtonElement,
	ComponentProps<typeof motion.button> & {
		'aria-label': string;
		title: string;
		icon: ReactNode;
		animationDelay?: number;
		variant?: 'default' | 'primary' | 'secondary' | 'accent';
	}
>(
	(
		{
			className,
			icon,
			animationDelay = 0,
			variant = 'default',
			initial,
			animate,
			disabled,
			...props
		},
		ref
	) => {
		const reduceMotion = useReducedMotion();
		const willChange = useWillChange();

		const { withOnButtonPress } = useHaptics();

		return (
			<motion.button
				ref={ref}
				initial={{
					scale: reduceMotion ? 1 : 0,
					...(typeof initial === 'object' ? initial : {}),
				}}
				animate={{
					scale: 1,
					filter: disabled ? 'blur(0.2rem)' : 'blur(0)',
					opacity: disabled ? 0.75 : 1,
					transition: {
						delay: animationDelay,
					},
					...(typeof animate === 'object' ? animate : {}),
				}}
				whileTap={{
					scale: 0.95,
				}}
				className={cn('rounded-full', className)}
				style={{
					willChange,
				}}
				disabled={disabled}
				{...withOnButtonPress}
				{...props}
			>
				<div className="relative">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						preserveAspectRatio="xMidYMid meet"
						viewBox="0 0 48 48"
						className={cn(
							'pointer-events-none absolute inset-0 size-full object-fill',
							variant === 'default' && 'text-black',
							variant === 'primary' && 'text-primary',
							variant === 'secondary' && 'text-secondary',
							variant === 'accent' && 'text-accent'
						)}
					>
						<path
							fill="currentColor"
							d="M19.2 47.2C16.2 46.2 9 45 5 38.5c-1-2.2-4-6.6-5-13.2C0 18.7 4 13.2 5 11l6.1-6.6 4-2.2c2-1.1 3-1.2 7.1-1.2h6l8.1 1.1 4 2.2 3.1 3.4c8 16.5 3 27.5 3 27.5-5 12-19.1 14.2-27.2 12Z"
						/>
					</svg>
					<div
						className={cn(
							'relative p-4 text-gray-50',
							variant === 'default' && 'text-gray-50',
							variant === 'primary' && 'text-white',
							variant === 'secondary' && 'text-white',
							variant === 'accent' && 'text-accentCompat'
						)}
					>
						{icon}
					</div>
				</div>
			</motion.button>
		);
	}
);

export default IconButton;
